import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, addDays, parse } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';

const timeSlots = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00'
];

const AddAvailabilityForm: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedSlots, setSelectedSlots] = useState<{[key: string]: boolean}>({});
  const [existingSlots, setExistingSlots] = useState<any[]>([]);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [slotDetails, setSlotDetails] = useState<any | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const navigate = useNavigate();

  const isTodayOrBefore = (date: Date | null) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateCopy = new Date(date);
    dateCopy.setHours(0, 0, 0, 0);
    return dateCopy.getTime() <= today.getTime();
  };

  const isMaxDateOrAfter = (date: Date | null) => {
    if (!date) return true;
    const maxDate = addDays(new Date(), 30);
    maxDate.setHours(23, 59, 59, 999);
    const dateCopy = new Date(date);
    dateCopy.setHours(23, 59, 59, 999);
    return dateCopy.getTime() >= maxDate.getTime();
  };

  const handlePrevDay = () => {
    if (selectedDate && !isTodayOrBefore(selectedDate)) {
      const prevDate = addDays(selectedDate, -1);
      setSelectedDate(prevDate);
      setSelectedSlots({});
    }
  };

  const handleNextDay = () => {
    if (selectedDate && !isMaxDateOrAfter(selectedDate)) {
      const nextDate = addDays(selectedDate, 1);
      setSelectedDate(nextDate);
      setSelectedSlots({});
    }
  };

  const handleSlotClick = (slot: string, existingSlot: any) => {
    if (existingSlot) {
      if (existingSlot.isBooked || existingSlot.status === 'Pending' || existingSlot.status === 'Approved' || existingSlot.status === 'Completed') {
        setSlotDetails({
          time: slot,
          ...existingSlot
        });
        setIsDetailsOpen(true);
      }
    } else {
      handleSlotToggle(slot);
    }
  };

  const fetchExistingSlots = async () => {
    if (!selectedDate) return;
    try {
      setIsLoadingExisting(true);
      const response = await api.getInstructorAvailabilitySlots();
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const filtered = response.data.filter((slot: any) => {
        const localDate = new Date(slot.startTime);
        return format(localDate, 'yyyy-MM-dd') === dateStr;
      });
      setExistingSlots(filtered);
    } catch (err) {
      console.error('Error fetching existing slots:', err);
    } finally {
      setIsLoadingExisting(false);
    }
  };

  useEffect(() => {
    fetchExistingSlots();
  }, [selectedDate]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    // Reset selected slots when date changes
    setSelectedSlots({});
  };

  const handleSlotToggle = (slot: string) => {
    setSelectedSlots(prev => ({
      ...prev,
      [slot]: !prev[slot]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedDate) {
      setError('Please select a date');
      return;
    }

    const selectedTimeSlots = Object.keys(selectedSlots).filter(slot => selectedSlots[slot]);
    
    if (selectedTimeSlots.length === 0) {
      setError('Please select at least one time slot');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      // Convert time slots to TimeSpan format
      const timeSlotsForApi = selectedTimeSlots.map(slot => {
        const [hours, minutes] = slot.split(':').map(Number);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
      });
      
      await api.generateTimeSlots(
        format(selectedDate, 'yyyy-MM-dd'),
        timeSlotsForApi
      );
      
      setSuccess(true);
      setSelectedSlots({}); // Clear temporary selections
      await fetchExistingSlots(); // Reload slots from database to display them immediately
    } catch (err) {
      console.error('Error creating availability slots:', err);
      setError('Error creating availability slots. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
        Add Availability
      </Typography>
      
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }}>
        <form onSubmit={handleSubmit}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton 
                  onClick={handlePrevDay} 
                  disabled={isTodayOrBefore(selectedDate)}
                  color="primary"
                  sx={{ border: '1px solid', borderColor: 'divider', p: 1.5 }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                
                <Box sx={{ flexGrow: 1 }}>
                  <DatePicker
                    label="Select Date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    minDate={new Date()}
                    maxDate={addDays(new Date(), 30)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true
                      }
                    }}
                  />
                </Box>
                
                <IconButton 
                  onClick={handleNextDay} 
                  disabled={isMaxDateOrAfter(selectedDate)}
                  color="primary"
                  sx={{ border: '1px solid', borderColor: 'divider', p: 1.5 }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                 <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1, color: '#37474f' }}>
                    Select Available Time Slots (Creates a 1-hour lesson starting from the selected time)
                  </FormLabel>
                  
                  {/* Legend */}
                  <Box display="flex" gap={1.5} flexWrap="wrap" sx={{ mb: 2, mt: 1 }}>
                    <Chip size="small" variant="outlined" label="Unavailable / Selectable" sx={{ bgcolor: '#fff', borderColor: '#e0e0e0', color: 'text.primary' }} />
                    <Chip size="small" label="Added as Available" sx={{ bgcolor: 'rgba(46, 125, 50, 0.08)', color: '#2e7d32', border: '1px solid #2e7d32', fontWeight: 'bold' }} />
                    <Chip size="small" label="Lesson Request (Pending)" sx={{ bgcolor: '#ed6c02', color: '#fff', fontWeight: 'bold' }} />
                    <Chip size="small" label="Lesson Approved / Booked" sx={{ bgcolor: '#1565c0', color: '#fff', fontWeight: 'bold' }} />
                    <Chip size="small" label="Lesson Completed" sx={{ bgcolor: '#757575', color: '#fff', fontWeight: 'bold' }} />
                  </Box>

                  {isLoadingExisting ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>Querying current availabilities...</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
                      gap: 2,
                      mt: 1
                    }}>
                      {timeSlots.map((slot) => {
                        const slotTime = parse(slot, 'HH:mm', new Date());
                        const endTime = format(
                          new Date(slotTime.getTime() + 60 * 60 * 1000), 
                          'HH:mm'
                        );
                        const isSelected = !!selectedSlots[slot];
                        
                        // Check if slot already exists in backend data (using timezone-safe Date object matching)
                        const existingSlot = existingSlots.find(s => {
                          const localDate = new Date(s.startTime);
                          const hrs = localDate.getHours().toString().padStart(2, '0');
                          const mins = localDate.getMinutes().toString().padStart(2, '0');
                          return `${hrs}:${mins}` === slot;
                        });

                        let buttonVariant: 'contained' | 'outlined' = isSelected ? 'contained' : 'outlined';
                        let buttonColor: any = isSelected ? 'primary' : 'inherit';
                        let buttonText = `${slot} - ${endTime}`;
                        let buttonStyles: any = {
                          py: 1.5,
                          borderRadius: 2,
                          fontWeight: 'bold',
                          fontSize: '0.9rem',
                          textTransform: 'none',
                          transition: 'all 0.2s',
                          width: '100%'
                        };

                        if (existingSlot) {
                          buttonStyles.cursor = 'not-allowed';
                          buttonColor = undefined; // Avoid MUI theme overriding custom styles
                          if (existingSlot.status === 'Pending') {
                            buttonVariant = 'contained';
                            buttonStyles.backgroundColor = '#ed6c02';
                            buttonStyles.color = '#fff';
                            buttonStyles.borderColor = '#ed6c02';
                            buttonStyles['&:hover'] = {
                              backgroundColor: '#d35400',
                              borderColor: '#d35400',
                            };
                            buttonText = `${slot} - ${endTime} (Request)`;
                          } else if (existingSlot.status === 'Approved') {
                            buttonVariant = 'contained';
                            buttonStyles.backgroundColor = '#1565c0';
                            buttonStyles.color = '#fff';
                            buttonStyles.borderColor = '#1565c0';
                            buttonStyles['&:hover'] = {
                              backgroundColor: '#0d47a1',
                              borderColor: '#0d47a1',
                            };
                            buttonText = `${slot} - ${endTime} (Booked)`;
                          } else if (existingSlot.status === 'Completed') {
                            buttonVariant = 'contained';
                            buttonStyles.backgroundColor = '#757575';
                            buttonStyles.color = '#fff';
                            buttonStyles.borderColor = '#757575';
                            buttonStyles['&:hover'] = {
                              backgroundColor: '#616161',
                              borderColor: '#616161',
                            };
                            buttonText = `${slot} - ${endTime} (Completed)`;
                          } else {
                            // Available
                            buttonVariant = 'outlined';
                            buttonStyles.borderColor = '#2e7d32';
                            buttonStyles.color = '#2e7d32';
                            buttonStyles.backgroundColor = 'rgba(46, 125, 50, 0.08)';
                            buttonStyles['&:hover'] = {
                              backgroundColor: 'rgba(46, 125, 50, 0.15)',
                              borderColor: '#2e7d32',
                            };
                            buttonText = `${slot} - ${endTime} (Available)`;
                          }
                        } else {
                          if (isSelected) {
                            buttonVariant = 'contained';
                            buttonColor = 'primary';
                            buttonStyles.color = '#fff';
                            buttonStyles.borderColor = 'primary.main';
                            buttonStyles.boxShadow = '0 4px 12px rgba(25, 118, 210, 0.2)';
                          } else {
                            buttonVariant = 'outlined';
                            buttonColor = 'inherit';
                            buttonStyles.color = 'text.primary';
                            buttonStyles.borderColor = '#e0e0e0';
                            buttonStyles.backgroundColor = '#fff';
                          }
                        }

                        return (
                          <Button
                            key={slot}
                            variant={buttonVariant}
                            color={buttonColor}
                            onClick={() => handleSlotClick(slot, existingSlot)}
                            sx={buttonStyles}
                          >
                            {buttonText}
                          </Button>
                        );
                      })}
                    </Box>
                  )}
                </FormControl>
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', width: '100%' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/instructor/dashboard')}
                    disabled={isSubmitting}
                    sx={{ borderRadius: 2, px: 3 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || Object.values(selectedSlots).every(slot => !slot)}
                    sx={{ borderRadius: 2, px: 4, fontWeight: 'bold' }}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Availabilities'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>
        </form>
      </Paper>
      
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%', borderRadius: 2 }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%', borderRadius: 2 }}>
          Availability time slots successfully created!
        </Alert>
      </Snackbar>

      <Dialog 
        open={isDetailsOpen} 
        onClose={() => setIsDetailsOpen(false)}
        PaperProps={{
          sx: { borderRadius: 3, p: 1, minWidth: '320px' }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1, color: '#1a237e' }}>
          Lesson Booking Details
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="caption" color="textSecondary">Time Slot</Typography>
              <Typography variant="body1" sx={{ fontWeight: '600' }}>
                {slotDetails?.time} - {slotDetails ? format(new Date(parse(slotDetails.time, 'HH:mm', new Date()).getTime() + 60 * 60 * 1000), 'HH:mm') : ''}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" color="textSecondary">Student Name</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                {slotDetails?.studentName || 'Unknown / Unspecified'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="textSecondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}>
                {slotDetails?.status === 'Pending' && (
                  <Chip size="small" label="Pending Approval (Lesson Request)" sx={{ bgcolor: '#ed6c02', color: '#fff', fontWeight: 'bold' }} />
                )}
                {slotDetails?.status === 'Approved' && (
                  <Chip size="small" label="Approved / Booked" sx={{ bgcolor: '#1565c0', color: '#fff', fontWeight: 'bold' }} />
                )}
                {slotDetails?.status === 'Completed' && (
                  <Chip size="small" label="Completed" sx={{ bgcolor: '#757575', color: '#fff', fontWeight: 'bold' }} />
                )}
              </Box>
            </Box>

            {slotDetails?.notes && (
              <Box>
                <Typography variant="caption" color="textSecondary">Notes</Typography>
                <Typography variant="body2" sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1.5, borderLeft: '3px solid #1a237e' }}>
                  {slotDetails.notes}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDetailsOpen(false)} variant="contained" sx={{ borderRadius: 2, px: 3 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddAvailabilityForm;
